import { createStore } from "@tellescope/store";
import { createFormStoreState, type FormStore } from "./form-store-definition";

export interface CreateFormStoreOptions {
  /** Unique across surfaces — it also namespaces the persisted keys. */
  name: string;
  /**
   * Where this surface's state lives. Omit for in-memory only, which is right for a form opened
   * inside the app; `"urlParams"` suits a deeplink whose state should survive a shared link.
   */
  persist?: "localStorage" | "sessionStorage" | "urlParams";
  /**
   * urlParams only: persist these keys as bare query params instead of namespacing them by store
   * name, so the store matches a link format that already exists.
   */
  urlParamKeys?: readonly string[];
}

/**
 * Declares one form store.
 *
 * Call this at module scope, once per surface that renders a form — never per render. Each surface
 * gets its own store so two open forms cannot overwrite each other; they differ only in the name and
 * persistence named here.
 *
 * @example
 * ```ts
 * export const { useStore: useFormLinkStore } = createFormStore({
 *   name: "form-link",
 *   persist: "urlParams",
 * })
 * ```
 */
export const createFormStore = ({ name, persist, urlParamKeys }: CreateFormStoreOptions) =>
  createStore<FormStore>(
    createFormStoreState,
    persist ? { name, persist, urlParamKeys, partialize: selectPersistableState } : { name }
  );

/**
 * What a persisted store writes out — named explicitly rather than spread, so state added later is
 * not persisted by accident.
 *
 * `pendingFilesByFieldId` is deliberately absent: `File` handles cannot survive serialization, and a
 * rehydrated one would be an empty object reported as unsaved work that no longer exists.
 */
const selectPersistableState = ({ logic }: FormStore) => ({ logic });
